import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';
import ProfileSidebar from './components/ProfileSidebar';
import BookingsTab from './components/BookingsTab';
import SavedToursTab from './components/SavedToursTab';
import AccountSettings from './components/AccountSettings';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    initialTab === 'saved' || initialTab === 'settings' ? initialTab : 'bookings',
  );

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'saved' || tab === 'settings') {
      setActiveTab(tab);
    }
  }, [searchParams]);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setUser(data.session?.user ?? null);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => {
      cancelled = true;
      listener.subscription.unsubscribe();
    };
  }, []);

  const handleSignOut = async () => {
    setSigningOut(true);
    await supabase.auth.signOut();
    setSigningOut(false);
    navigate('/');
  };

  const fullName = (user?.user_metadata?.full_name as string) || '';
  const email = user?.email || '';

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1 w-full px-4 md:px-8 lg:px-12 pt-24 md:pt-28 pb-16 md:pb-20">
        {loading ? (
          <div className="flex items-center justify-center py-40">
            <div className="flex flex-col items-center gap-3 text-secondary-500">
              <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
              <span className="text-sm">{t('profile.loading')}</span>
            </div>
          </div>
        ) : !user ? (
          <div className="max-w-md mx-auto text-center py-20">
            <div className="w-16 h-16 mx-auto rounded-full bg-secondary-100 flex items-center justify-center">
              <i className="ri-user-line text-2xl text-secondary-500" />
            </div>
            <h1 className="mt-6 font-heading text-2xl font-bold text-secondary-900">
              {t('profile.signInToView')}
            </h1>
            <p className="mt-2 text-sm text-secondary-500 leading-relaxed">
              {t('profile.manageText')}
            </p>
            <Link
              to="/login"
              className="mt-6 inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm rounded-md px-6 py-3 transition-colors whitespace-nowrap"
            >
              {t('auth.signIn')}
              <i className="ri-arrow-right-line text-sm" />
            </Link>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-secondary-900">
                {t('profile.myAccount')}
              </h1>
              <p className="mt-2 text-sm text-secondary-500">
                {t('profile.welcomeBack')}
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
              <ProfileSidebar
                activeTab={activeTab}
                onTabChange={setActiveTab}
                fullName={fullName}
                email={email}
                onSignOut={handleSignOut}
                signingOut={signingOut}
              />

              <div className="flex-1 min-w-0">
                {activeTab === 'bookings' && <BookingsTab />}
                {activeTab === 'saved' && <SavedToursTab />}
                {activeTab === 'settings' && (
                  <AccountSettings fullName={fullName} email={email} />
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}